'use client';

import { IMediaItem, IMediaMetadata } from '@/lib/types/media/core';
import { Input, TextArea } from '@heroui/react';
import { Button } from '@/components/ui/button';
import { useCallback, useState } from 'react';

export interface MediaMetadataFormProps {
  media: IMediaItem;
  onSave: (metadata: Partial<IMediaMetadata>) => Promise<void>;
  onCancel?: () => void;
}

export function MediaMetadataForm({
  media,
  onSave,
  onCancel
}: MediaMetadataFormProps) {
  const [metadata, setMetadata] = useState<Partial<IMediaMetadata>>({
    alt: media.metadata.alt,
    caption: media.metadata.caption,
    description: media.metadata.description
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await onSave(metadata);
    } finally {
      setIsSubmitting(false);
    }
  }, [metadata, onSave]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="alt" className="block text-sm font-medium text-gray-700">
          Alt Text
        </label>
        <Input
          id="alt"
          value={metadata.alt || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setMetadata(prev => ({ ...prev, alt: e.target.value }))
          }
          placeholder="Describe the media for accessibility"
          className="mt-1"
        />
      </div>

      <div>
        <label htmlFor="caption" className="block text-sm font-medium text-gray-700">
          Caption
        </label>
        <Input
          id="caption"
          value={metadata.caption || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setMetadata(prev => ({ ...prev, caption: e.target.value }))
          }
          placeholder="Add a caption"
          className="mt-1"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <TextArea
          id="description"
          value={metadata.description || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setMetadata(prev => ({ ...prev, description: e.target.value }))
          }
          placeholder="Add a detailed description"
          rows={3}
          className="mt-1"
        />
      </div>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            onPress={onCancel}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
        >
          Save Changes
        </Button>
      </div>
    </form>
  );
}
